import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import es from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { supabase } from '../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaBed, FaFilter } from 'react-icons/fa';
import ReservationModal from '../components/ReservationModal';
import AdminSidebar from '../components/AdminSidebar';

const locales = {
  'es': es,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }), // Lunes
  getDay,
  locales,
});

// Define static room resources
const ROOM_RESOURCES = Array.from({ length: 12 }, (_, i) => ({
  resourceId: i + 1,
  resourceTitle: `Habitación ${i + 1}`,
}));

const AdminCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const navigate = useNavigate();

  const checkAuth = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('reservations')
        .select('*')
        .in('status', ['confirmed']);

      if (error) throw error;

      const calendarEvents = data.flatMap(reservation => {
        const events = [];
        if (reservation.assigned_room_number && Array.isArray(reservation.assigned_room_number)) {
          reservation.assigned_room_number.forEach(roomNumber => {
            events.push({
              id: `${reservation.id}-${roomNumber}`,
              title: `${reservation.full_name}`,
              start: new Date(reservation.checkin_date),
              end: new Date(reservation.checkout_date),
              allDay: true,
              resourceId: roomNumber, // Assign event to a specific room resource
              reservationDetails: reservation, // Store full reservation for modal
            });
          });
        }
        return events;
      });

      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
    fetchReservations();
  }, [checkAuth, fetchReservations]);

  const handleSelectEvent = (event) => {
    // When clicking an event, open the modal with its full reservation details
    if (event.reservationDetails) {
      setSelectedReservation(event.reservationDetails);
      setShowModal(true);
    }
  };
  
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReservation(null);
    fetchReservations(); // Refetch reservations in case any changes were made
  };

  const eventStyleGetter = (event, start, end, isSelected) => {
    let backgroundColor = '#3174ad';
    // Style can be based on reservationDetails.status if needed
    if (event.reservationDetails?.status === 'confirmed') {
      backgroundColor = '#28a745';
    }
    const style = {
      backgroundColor,
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block'
    };
    return {
      style: style
    };
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      <AdminSidebar />
      
      <div className="flex-1 w-full md:w-auto">
        {/* Header */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8 py-4 md:py-6">
            <div className="flex items-center gap-2 md:gap-3">
              <FaCalendarAlt className="text-purple-600 flex-shrink-0" size={24} />
              <div className="min-w-0">
                <h1 className="text-xl md:text-2xl lg:text-3xl font-playfair font-bold text-gray-900 truncate">
                  Calendario de Reservas
                </h1>
                <p className="text-xs md:text-sm text-gray-600 mt-1">Vista general de ocupación por habitación</p>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="px-4 sm:px-6 lg:px-8 py-3 md:py-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 md:p-4">
            <div className="flex items-center gap-2 mb-2 md:mb-3">
              <FaFilter className="text-gray-600 flex-shrink-0" size={14} />
              <h3 className="font-semibold text-gray-900 text-sm md:text-base">Leyenda de Estados</h3>
            </div>
            <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 md:w-4 md:h-4 bg-yellow-400 rounded flex-shrink-0"></div>
                <span className="text-xs md:text-sm text-gray-700">Pendiente</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 md:w-4 md:h-4 bg-green-500 rounded flex-shrink-0"></div>
                <span className="text-xs md:text-sm text-gray-700">Confirmada</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded flex-shrink-0"></div>
                <span className="text-xs md:text-sm text-gray-700">Cancelada</span>
              </div>
              <div className="flex items-center gap-2">
                <FaBed className="text-purple-600 flex-shrink-0" size={14} />
                <span className="text-xs md:text-sm text-gray-700">12 Habitaciones</span>
              </div>
            </div>
          </div>
        </div>

        {/* Calendar */}
        <div className="px-4 sm:px-6 lg:px-8 pb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 md:p-4 lg:p-6 overflow-x-auto" style={{ height: '60vh', minHeight: '400px' }}>
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 text-sm md:text-base">Cargando calendario...</p>
                </div>
              </div>
            ) : (
              <div className="h-full min-w-[300px]">
                <Calendar
                  localizer={localizer}
                  events={events}
                  resources={ROOM_RESOURCES}
                  resourceIdAccessor="resourceId"
                  resourceTitleAccessor="resourceTitle"
                  defaultView="month"
                  views={['month', 'agenda']}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: '100%' }}
                  onSelectEvent={handleSelectEvent}
                  popup
                  messages={{
                    next: "Siguiente",
                    previous: "Anterior",
                    today: "Hoy",
                    month: "Mes",
                    week: "Semana",
                    day: "Día",
                    agenda: "Agenda",
                    date: "Fecha",
                    time: "Hora",
                    event: "Evento",
                    noEventsInRange: "No hay reservas en este rango"
                  }}
                  culture="es"
                  eventPropGetter={eventStyleGetter}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      
      <ReservationModal
        isOpen={showModal}
        onClose={handleCloseModal}
        isAdmin={true}
        existingReservation={selectedReservation}
      />
    </div>
  );
};

export default AdminCalendar;
